// Mints a fresh reCAPTCHA v3 token before a form posts one Google would reject.
//
// The recaptcha gem mints each v3 token on page load. Google expires it after two
// minutes and accepts it once, so a slow or repeated submit would post a dead token
// and get the checkbox. A capture listener on window, ahead of every other submit
// handler, stops a submit whose token is stale or already posted, mints a new one
// with grecaptcha.execute, and submits again with the same submitter. It fails open:
// after WAIT_MS the form posts the token it had, and the server judges it as before.

const FIELD_PREFIX = 'g-recaptcha-response-data['
const WAIT_MS = 3000
// Google's limit is two minutes; re-mint with room to spare for the round trip.
const FRESH_MS = 90_000

let installed = false
const minting = new WeakSet()
const resubmitting = new WeakSet()

// Empty means the page-load mint has not finished. Staleness is wall-clock time, as
// Google's expiry is; a page-load token counts as minted at navigation start.
function needsMint(field) {
  const mintedAt = Number(
    field.dataset.recaptchaMintedAt ?? performance.timeOrigin,
  )
  return (
    !field.value ||
    field.dataset.recaptchaSpent === 'true' ||
    Date.now() - mintedAt > FRESH_MS
  )
}

function execute(field) {
  const { grecaptcha } = window
  const siteKey = field.dataset.sitekey
  if (!siteKey || typeof grecaptcha?.execute !== 'function')
    return Promise.resolve()

  const action = field.name.slice(FIELD_PREFIX.length, -1)
  return new Promise((resolve, reject) => {
    grecaptcha.ready(() => {
      Promise.resolve()
        .then(() => grecaptcha.execute(siteKey, { action }))
        .then(resolve, reject)
    })
  })
}

async function mint(field) {
  let timer
  const timeout = new Promise((resolve) => {
    timer = window.setTimeout(resolve, WAIT_MS)
  })
  try {
    const token = await Promise.race([execute(field), timeout])
    if (typeof token === 'string' && token) {
      field.value = token
      field.dataset.recaptchaMintedAt = String(Date.now())
      delete field.dataset.recaptchaSpent
    }
  } finally {
    window.clearTimeout(timer)
  }
}

function resubmit(form, submitter) {
  resubmitting.add(form)
  try {
    form.requestSubmit(submitter?.form === form ? submitter : undefined)
  } finally {
    resubmitting.delete(form)
  }
}

export function refreshRecaptchaV3OnSubmit() {
  if (installed) return
  installed = true

  window.addEventListener(
    'submit',
    (event) => {
      const form = event.target
      if (!(form instanceof HTMLFormElement) || event.defaultPrevented) return

      const fields = [
        ...form.querySelectorAll(`input[name^="${FIELD_PREFIX}"]`),
      ]
      if (fields.length === 0) return

      // The server checks a solved checkbox only when v3 fails, so a fresh v3
      // token would skip it and never record that the checkbox was solved.
      const solvedCheckbox = form.querySelector(
        '[name="g-recaptcha-response"]',
      )?.value
      if (
        resubmitting.has(form) ||
        solvedCheckbox ||
        typeof form.requestSubmit !== 'function' ||
        !fields.some(needsMint)
      ) {
        // This submit hands the tokens on to be posted, so they are spent.
        fields.forEach((field) => {
          field.dataset.recaptchaSpent = 'true'
        })
        return
      }

      event.preventDefault()
      event.stopImmediatePropagation()
      // A second click while minting is dropped, not queued.
      if (minting.has(form)) return

      minting.add(form)
      Promise.allSettled(fields.filter(needsMint).map(mint)).then(() => {
        // Browsers ignore requestSubmit while still firing the first submit's
        // events, which is now if minting settled at once; a task later it is over.
        window.setTimeout(() => {
          minting.delete(form)
          if (form.isConnected) resubmit(form, event.submitter)
        })
      })
    },
    true,
  )
}
